import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];
  #passedHero = false;
  #handleResize = null;

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */

//   #observeStickyPosition = () => {
//   if (this.#intersectionObserver) this.#intersectionObserver.disconnect();

//   const hero = document.querySelector('#top-hero-section');
//   if (!hero) return;

//   this.#passedHero = false;

//   this.#handleResize = () => {
//     const headerHeight = this.offsetHeight;
//     const rootMarginValue = `-${headerHeight}px 0px 0px 0px`;

//     if (this.#intersectionObserver) {
//       this.#intersectionObserver.disconnect();
//     }

//     this.#intersectionObserver = new IntersectionObserver(([entry]) => {
//       if (!entry) return;

//       const scrollTop = window.scrollY;

//       if (scrollTop < 10) {
//         this.#passedHero = false;
//         this.dataset.stickyState = 'inactive';
//         this.classList.remove('scrolled-past-trigger');
//         return;
//       }

//       if (entry.isIntersecting) {
//         this.#passedHero = false;
//         this.dataset.stickyState = 'covered';
//         this.classList.remove('scrolled-past-trigger');
//       } else {
//         // Hero gone → enable scroll system
//         this.#passedHero = true;
//         this.dataset.stickyState = 'idle';
//         this.classList.add('scrolled-past-trigger');
//       }
//     }, {
//       threshold: 0,
//       rootMargin: rootMarginValue
//     });

//     this.#intersectionObserver.observe(hero);
//   };

//   // init
//   this.#handleResize();

//   window.addEventListener('resize', this.#handleResize);
// };

#observeStickyPosition = () => {
  if (this.#intersectionObserver) this.#intersectionObserver.disconnect();

  const hero = document.querySelector('#top-hero-section');
  const stickyTrigger = document.querySelector('#sticky-nav-start');

  if (!hero && !stickyTrigger) return;

  this.#passedHero = false; // whether any trigger has passed

  this.#handleResize = () => {
    const headerHeight = this.offsetHeight;
    const rootMarginValue = `-${headerHeight}px 0px 0px 0px`;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      let anyTriggerPassed = false;

      entries.forEach(entry => {
        // If element has left viewport, mark passed
        if (!entry.isIntersecting) {
          anyTriggerPassed = true;
        }
      });

      const scrollTop = window.scrollY;

      if (scrollTop < 10) {
        this.#passedHero = false;
        this.dataset.stickyState = 'inactive';
        this.classList.remove('scrolled-past-trigger');
        return;
      }

      if (anyTriggerPassed) {
        this.#passedHero = true;
        this.dataset.stickyState = 'idle';
        this.classList.add('scrolled-past-trigger');
      } else {
        this.#passedHero = false;
        this.dataset.stickyState = 'covered';
        this.classList.remove('scrolled-past-trigger');
      }
    }, {
      threshold: 0,
      rootMargin: rootMarginValue
    });

    // Observe both if they exist
    if (hero) this.#intersectionObserver.observe(hero);
    if (stickyTrigger) this.#intersectionObserver.observe(stickyTrigger);
  };

  // init
  this.#handleResize();
  window.addEventListener('resize', this.#handleResize);
};

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = scrollTop <= 0 || headerTop >= 0; // ✅ at top of page

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.dataset.scrollDirection = isAtTop ? 'none' : isScrollingUp ? 'up' : 'down';
      if (isAtTop) this.classList.remove('scrolled-past-trigger'); // reset color
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#passedHero) {
      this.dataset.stickyState = 'covered';
      this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
      if (isAtTop) this.classList.remove('scrolled-past-trigger'); // reset to cream
    } else {
      if (isScrollingUp) {
        if (isAtTop) {
          this.#offscreen = false;
          this.dataset.stickyState = 'covered'; // ✅ reset to cream
          this.dataset.scrollDirection = 'none';
          this.classList.remove('scrolled-past-trigger');
        } else {
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
          this.classList.add('scrolled-past-trigger'); // sticky = white
        }
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'down';
        this.classList.add('scrolled-past-trigger'); // sticky = white
      }
    }

    this.#lastScrollTop = scrollTop;
  };


// #updateScrollState = () => {
//   const stickyMode = this.getAttribute('sticky');
//   const scrollTop = document.scrollingElement?.scrollTop ?? 0;
//   const headerTop = this.getBoundingClientRect().top;
//   const isScrollingUp = scrollTop < this.#lastScrollTop;
//   const isAtTop = headerTop >= 0;

//   if (this.#timeout) {
//     clearTimeout(this.#timeout);
//     this.#timeout = null;
//   }

//   if (stickyMode === 'always') {
//     if (isAtTop) {
//       this.dataset.scrollDirection = 'none';
//     } else if (isScrollingUp) {
//       this.dataset.scrollDirection = 'up';
//     } else {
//       this.dataset.scrollDirection = 'down';
//     }
//     this.#lastScrollTop = scrollTop;
//     return;
//   }

//   if (!this.#passedHero) {
//     this.dataset.stickyState = 'covered';
//     this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
//   } else {
//     if (isScrollingUp) {
//       if (isAtTop) {
//         this.#offscreen = false;
//         this.dataset.stickyState = 'inactive';
//         this.dataset.scrollDirection = 'none';
//       } else {
//         this.dataset.stickyState = 'active';
//         this.dataset.scrollDirection = 'up';
//       }
//     } else {
//       this.dataset.stickyState = 'active';
//       this.dataset.scrollDirection = 'down';
//     }
//   }

//   this.#lastScrollTop = scrollTop;
// };

    connectedCallback() {
      super.connectedCallback();
      this.#resizeObserver.observe(this);
      this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

      const stickyMode = this.getAttribute('sticky');

      if (stickyMode) {
        this.#observeStickyPosition(false);

        if (stickyMode === 'scroll-up' || stickyMode === 'always') {
          document.addEventListener('scroll', this.#handleWindowScroll);
        }
      }
    }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#handleResize);
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
